import { useEffect, useRef } from 'react';
import { cn } from '@immo/ui-kit';

// IMM-849.1: the form-level error slot — the "something went wrong with this
// submit" message at the foot of a dialog or page — had no home at all. The
// idiom (an inline null guard around a bare danger-coloured paragraph) was
// hand-copied at 80 sites across 73 files, none of which announced: no
// `role="alert"`, no `aria-live`. A screen-reader user learned the submit had
// failed only by hunting the page.
//
// BL-117's operative rule is that the announcement attribute belongs in the
// shared component that renders the slot, never at the call site — so this is
// that component. `apps/web-app/src/components/form/form-field.tsx:24` and
// `apps/admin-web-app/src/components/form/form-field.tsx:44` are the same
// decision for the FIELD slot; this owns the form-level one.

type FormBannerProps = {
  /** Already-translated message, or null when the form has no form-level error. */
  readonly error: string | null;
  /**
   * Closed scale — the migrated call sites use exactly these three. It is a
   * prop and NOT a `className` override on purpose: `text-base` vs
   * `text-base-sm` are variants on a custom token scale that `tailwind-merge`
   * cannot classify, so an override would not win the cascade by specificity,
   * only by the stylesheet's emission order (BL-103).
   */
  readonly size?: 'base' | 'base-sm' | 'sm';
  /** Spacing ONLY (`mt-2` | `mt-3` | `mt-4`). Never colour, never size. */
  readonly className?: string;
};

const SIZE = { base: 'text-base', 'base-sm': 'text-base-sm', sm: 'text-sm' } as const;

export function FormBanner({ error, size = 'base', className }: FormBannerProps) {
  const ref = useRef<HTMLParagraphElement>(null);

  // Above the early return (hook order); the null check moves inside. `nearest`
  // is a no-op for a banner already fully visible, so the form never jumps.
  useEffect(() => {
    if (error === null) return;

    ref.current?.scrollIntoView({ block: 'nearest' });
  }, [error]);

  // The null branch lives INSIDE, so no call site keeps half the pair as an
  // external `&&` — otherwise the seam splits again at the next surface
  // (BL-110) and the sole-home guard has nothing to key on.
  if (error === null) return null;

  // `role="alert"` sits on the CONDITIONAL element, never on a permanently
  // mounted wrapper: a wrapper carrying the role announces on every render.
  // No `id` and no `aria-describedby` — a form-level banner names no control,
  // so an association here would invent a relationship. `FormField` keeps that
  // duty for field errors.
  return (
    <p ref={ref} role="alert" className={cn(SIZE[size], 'text-danger-600', className)}>
      {error}
    </p>
  );
}
